/**
 * ClientsListController — page /clients.html (EPIC-009 / 9.4, épuré en 99.55).
 *
 * Hydrate la liste paginée des clients depuis `GET /clients` et
 * permet : recherche (debounce 300 ms), filtres type/statut, tri sur
 * Nom (seul champ whitelisté côté 9.2 backend) et pagination.
 *
 * Colonnes (99.55) : Nom · Type (pill coloré) · SIRET/SIREN · Ville ·
 * Dernière activité · « Voir ». L'archivage / la restauration ne sont plus
 * pilotés depuis la liste — ils se font sur la fiche `clients-detail.html`.
 *
 * Le badge « mauvais payeur » est rendu depuis `paymentScore.badPayerBadge`
 * (calculé en batch par 9.3 `ClientPaymentScoreService::computeBadPayerBadgesFor`).
 * La ville vient de `addresses[]` (adresse de facturation prioritaire),
 * hydratée en batch côté API (99.55) — `_pickCity`.
 *
 * Convention CLAUDE.md respectée : pas d'HTML inline en JS (clone du
 * `<template>` + remplissage data-slot + setAttribute). Aucune classe CSS
 * inventée — toutes les classes utilisées sont dans notamicro.css.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const TYPE_LABELS = {
  b2b: "Entreprise",
  b2c: "Particulier",
  public_administration: "Administration",
};

// Couleur de la pill « Type » — le rouge reste réservé au badge « mauvais
// payeur » de la même ligne (pas de collision). 99.55.
const TYPE_PILL_CLASSES = {
  b2b: "nm-pill--blue",
  b2c: "nm-pill--olive",
  public_administration: "nm-pill--ochre",
};

export default class ClientsListController extends BaseController {
  static targets = [
    "tableWrap",
    "tableBody",
    "loadingRow",
    "summary",
    "searchInput",
    "typeFilter",
    "statusFilter",
    "pagination",
    "pageInfo",
    "pageButtons",
  ];

  static values = {
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
    q: { type: String, default: "" },
    sort: { type: String, default: "legalName" },
    statusFilter: { type: String, default: "active" },
    typeFilter: { type: String, default: "" },
  };

  connect() {
    this._debounceTimer = null;
    this._fetchSeq = 0;
    this.refresh();
    this._hydrateDemoFlag();
  }

  /**
   * EPIC-009 / 9.7 — Pose `data-demo="true"` sur le `<main>` de la page
   * si l'organisation est en compte démo (`onboardingStage ===
   * 'pre_inpi_payment'`). La règle CSS `[data-demo="true"] [data-demo-only]`
   * révèle alors le badge « démo » présent en markup à chaque ligne client.
   * Idempotent — un seul fetch `/auth/me` par page via `api.getMeCached()`,
   * mutualisé avec `app-header`.
   */
  async _hydrateDemoFlag() {
    try {
      const me = await api.getMeCached();
      if (me?.organization?.onboardingStage === "pre_inpi_payment") {
        const main = document.querySelector("main.nm-app__main");
        if (main) main.setAttribute("data-demo", "true");
      }
    } catch (_error) {
      // /auth/me a échoué (401 déjà redirigé par api.js, autres erreurs
      // silencieuses) — l'absence de badge en cas d'échec est sans danger.
    }
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  // ---------------------------------------------------------------------------
  // Filter / search / sort / paginate actions
  // ---------------------------------------------------------------------------

  search(event) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    const value = event.target.value;
    this._debounceTimer = setTimeout(() => {
      this.qValue = value;
      this.pageValue = 1;
      this.refresh();
    }, 300);
  }

  changeType(event) {
    this.typeFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  changeStatus(event) {
    this.statusFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  sort(event) {
    const button = event.currentTarget;
    const field = button.dataset.sortField;
    if (!field) return;
    const current = button.getAttribute("aria-sort");
    const nextDirection = current === "ascending" ? "descending" : "ascending";
    this._updateSortHeaders(field, nextDirection);
    this.sortValue = nextDirection === "descending" ? `-${field}` : field;
    this.pageValue = 1;
    this.refresh();
  }

  paginate(event) {
    const page = Number(event.currentTarget.dataset.page);
    if (!Number.isFinite(page) || page < 1) return;
    this.pageValue = page;
    this.refresh();
  }

  reset() {
    // L'événement `reset` est dispatché AVANT que le navigateur ne restaure
    // les valeurs par défaut des champs — on ne peut donc pas les lire ici.
    // On pose explicitement les valeurs par défaut (values stockées + champs
    // DOM) puis on refresh, sans dépendre du timing du reset natif.
    this.typeFilterValue = "";
    this.statusFilterValue = "active";
    this.qValue = "";
    this.sortValue = "legalName";
    this.pageValue = 1;
    if (this.hasTypeFilterTarget) this.typeFilterTarget.value = "";
    if (this.hasStatusFilterTarget) this.statusFilterTarget.value = "active";
    if (this.hasSearchInputTarget) this.searchInputTarget.value = "";
    this._updateSortHeaders("legalName", "ascending");
    this.refresh();
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    const seq = ++this._fetchSeq;
    if (this.hasTableWrapTarget) this.tableWrapTarget.setAttribute("aria-busy", "true");

    const params = { page: this.pageValue, perPage: this.perPageValue };
    if (this.qValue) params.q = this.qValue;
    if (this.sortValue) params.sort = this.sortValue;
    if (this.statusFilterValue) params["filter[status]"] = this.statusFilterValue;
    if (this.typeFilterValue) params["filter[type]"] = this.typeFilterValue;

    try {
      const response = await api.listClients(params);
      // Ignore stale responses (un fetch plus récent est en vol).
      if (seq !== this._fetchSeq) return;
      const clients = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination ?? { page: 1, perPage: this.perPageValue, total: 0, totalPages: 1 };
      this._renderRows(clients);
      this._renderSummary(pagination);
      this._renderPagination(pagination);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les clients.";
      this.showError(msg);
    } finally {
      if (seq === this._fetchSeq && this.hasTableWrapTarget) {
        this.tableWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  _renderRows(clients) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    if (clients.length === 0) {
      this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-client-empty"));
      return;
    }

    for (const client of clients) {
      const fragment = this.cloneTemplate("tpl-client-row");
      const detailUrl = `/clients-detail.html?id=${encodeURIComponent(client.id)}`;
      const isArchived = client.status === "archived";

      this.fillSlots(fragment, {
        legalName: client.legalName ?? "—",
        typeLabel: TYPE_LABELS[client.type] ?? "—",
        siretSiren: client.siret ?? client.siren ?? "—",
        city: this._pickCity(client.addresses),
        lastActivity: client.archivedAt
          ? this.formatShortDate(client.archivedAt)
          : this.formatShortDate(client.createdAt),
      });

      // Pose la couleur de la pill « Type » (le label est déjà rempli par
      // le slot ci-dessus). Type inconnu → pill neutre sans modificateur.
      const typePill = fragment.querySelector('[data-slot-element="typePill"]');
      if (typePill) {
        const pillClass = TYPE_PILL_CLASSES[client.type];
        if (pillClass) typePill.classList.add(pillClass);
      }

      // Le lien « Voir » (et le nom) pointe vers la fiche détail.
      fragment.querySelectorAll('[data-slot-attr-href="detailUrl"]').forEach((el) => {
        el.setAttribute("href", detailUrl);
      });
      const viewBtn = fragment.querySelector('[data-slot-attr-aria-label="viewLabel"]');
      if (viewBtn) {
        viewBtn.setAttribute("aria-label", `Voir la fiche de ${client.legalName ?? "ce client"}`);
      }

      const badge = fragment.querySelector('[data-slot-element="badPayerBadge"]');
      if (badge) {
        badge.hidden = !(client.paymentScore && client.paymentScore.badPayerBadge);
      }

      // Ligne grisée pour les clients archivés (style défini dans
      // notamicro.css : `.nm-table tbody tr.is-archived td`).
      if (isArchived) {
        const tr = fragment.querySelector("tr");
        if (tr) tr.classList.add("is-archived");
      }

      this.tableBodyTarget.appendChild(fragment);
    }
  }

  _renderSummary(pagination) {
    if (!this.hasSummaryTarget) return;
    const total = Number(pagination.total) || 0;
    const label = total === 0
      ? "Aucun client"
      : `${total} client${total > 1 ? "s" : ""}`;
    this.summaryTarget.textContent = label;
  }

  _renderPagination(pagination) {
    if (!this.hasPaginationTarget || !this.hasPageButtonsTarget || !this.hasPageInfoTarget) {
      return;
    }
    const page = Number(pagination.page) || 1;
    const perPage = Number(pagination.perPage) || this.perPageValue;
    const total = Number(pagination.total) || 0;
    const totalPages = Number(pagination.totalPages) || 1;

    if (total === 0 || totalPages <= 1) {
      this.paginationTarget.hidden = true;
      return;
    }
    this.paginationTarget.hidden = false;

    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, total);
    this.pageInfoTarget.textContent = `${start} — ${end} sur ${total} client${total > 1 ? "s" : ""}`;

    // Rebuild buttons.
    while (this.pageButtonsTarget.firstChild) {
      this.pageButtonsTarget.removeChild(this.pageButtonsTarget.firstChild);
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page - 1, text: "‹", label: "Page précédente", disabled: page <= 1,
    }));
    for (const token of this.getPagesToShow(page, totalPages)) {
      if (token === "...") {
        this.pageButtonsTarget.appendChild(this.cloneTemplate("tpl-pagination-ellipsis"));
        continue;
      }
      this.pageButtonsTarget.appendChild(this._buildPageButton({
        page: token,
        text: String(token),
        label: `Page ${token}`,
        current: token === page,
      }));
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page + 1, text: "›", label: "Page suivante", disabled: page >= totalPages,
    }));
  }

  _buildPageButton({ page, text, label, current = false, disabled = false }) {
    const fragment = this.cloneTemplate("tpl-pagination-page");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.textContent = text;
    btn.setAttribute("aria-label", label);
    btn.dataset.page = String(page);
    if (disabled) btn.disabled = true;
    if (current) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "page");
    }
    return fragment;
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-client-error"));
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "Erreur de chargement";
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _pickCity(addresses) {
    if (!Array.isArray(addresses) || addresses.length === 0) return "—";
    const billing = addresses.find((a) => a.type === "billing");
    const picked = billing ?? addresses[0];
    return picked?.city || "—";
  }

  _updateSortHeaders(activeField, direction) {
    const buttons = this.element.querySelectorAll(".nm-th-sort[data-sort-field]");
    buttons.forEach((btn) => {
      if (btn.dataset.sortField === activeField) {
        btn.setAttribute("aria-sort", direction);
      } else {
        btn.setAttribute("aria-sort", "none");
      }
    });
  }
}
